import makeApiCall from "./master/axiosIndex";

// Machinery Norms master — a FLAT rate card (one row per Activity + Norm Code +
// Description). Careful clone of the Manpower Norms master (CRUD + Excel upload
// + async export + duplicate-to-project + history) WITHOUT the apply-to-task
// engine. The rate depends on Activity + Norm Code: at project scope the
// activity resolves to a real project activity (activityId FK), at org scope
// (projectId 0) the Activity Name is free text and activityId stays null.
// Decimals (totalLead, rate) travel as strings — parse on the consumer side.
export const machineryApis = {
  create: (data: {
    // Resolved project-activity FK — project scope only; omitted at org scope.
    activityId?: number | null;
    // As-entered activity label; always sent (it is part of the unique key).
    activityName: string;
    normCode: string;
    description?: string;
    nearestDump?: string;
    totalLead?: number;
    rate?: number;
    uomId?: number | null;
    // Scope of the new row: category head (e.g. MUCKING) + projectId (0 = org
    // master). Both are immutable once created, so they ride on create only.
    category?: string;
    projectId?: number;
  }): Promise<any> => {
    return makeApiCall({ url: "/machinery-norms/create", method: "post", data });
  },
  update: (data: {
    machineryNormId: number;
    activityId?: number | null;
    activityName?: string;
    normCode?: string;
    description?: string;
    nearestDump?: string;
    totalLead?: number;
    rate?: number;
    uomId?: number | null;
  }): Promise<any> => {
    return makeApiCall({ url: "/machinery-norms/update", method: "put", data });
  },
  activate: (machineryNormId: any): Promise<any> => {
    return makeApiCall({
      url: "/machinery-norms/activate",
      method: "patch",
      data: { machineryNormId },
    });
  },
  deactivate: (machineryNormId: any): Promise<any> => {
    return makeApiCall({
      url: "/machinery-norms/deactivate",
      method: "patch",
      data: { machineryNormId },
    });
  },
  getById: (machineryNormId: any): Promise<any> => {
    return makeApiCall({
      url: `/machinery-norms/id/${machineryNormId}`,
      method: "get",
    });
  },
  getDropdown: (): Promise<any> => {
    return makeApiCall({ url: "/machinery-norms/dropdown", method: "get" });
  },
  getList: (data: any): Promise<any> => {
    return makeApiCall({ url: "/machinery-norms/list", method: "get", params: data });
  },
  uploadExcel: (data: FormData): Promise<any> => {
    return makeApiCall({
      url: "/machinery-norms/upload/excel",
      method: "post",
      data,
    });
  },
  // Async master export: enqueue a build job (worker builds the xlsx and stores
  // it in DMS). Returns the request reportId. The build is scoped to the given
  // projectId (0 = org master) + category head (e.g. MUCKING).
  requestExport: (
    comment?: string,
    projectId?: number,
    category?: string
  ): Promise<any> => {
    return makeApiCall({
      url: "/machinery-norms/export/request",
      method: "post",
      data: { comment, projectId, category },
    });
  },
  // Copies the organization-master set (projectId 0) into the given project.
  // Returns { insertedCount, message }.
  duplicateToProject: (projectId: number): Promise<any> => {
    return makeApiCall({
      url: "/machinery-norms/duplicate",
      method: "post",
      data: { projectId },
    });
  },
  // Recent machinery norms export requests (poll target). Processed rows carry filePath.
  fetchExportList: (): Promise<any> => {
    return makeApiCall({ url: "/machinery-norms/export/list", method: "get" });
  },

  // ---- Apply machinery norms to a task (machinery requirement) ----
  // Applies a set of machinery norms to a task: each id → one requirement row
  // (appliedQty = task workQuantity or override — no factor, rate = norm rate).
  // qty 0 is valid; negatives error; re-applying the same norm is rejected
  // server-side. Returns { applied, items, skipped }.
  applyToTask: (data: {
    taskId: number;
    machineryNormIds: number[];
    overrideQty?: number;
  }): Promise<any> => {
    return makeApiCall({ url: "/machinery-norms/task/apply", method: "post", data });
  },
  // Removes one applied machinery norm from a task (soft-delete + reason).
  removeFromTask: (data: {
    taskId: number;
    machineryNormId: number;
    reason?: string;
  }): Promise<any> => {
    return makeApiCall({ url: "/machinery-norms/task/remove", method: "post", data });
  },
  // Flat list of machinery norms applied to a task. Returns { items: [...] }.
  getTaskMachineryNorms: (taskId: number): Promise<any> => {
    return makeApiCall({ url: `/machinery-norms/task/${taskId}/machinery`, method: "get" });
  },
  // The norms that CAN be applied to this task — only the task's own
  // project+activity (all category heads). Returns { workQuantity, workUom,
  // activityId, activityName, items: [...] }. Decimals arrive as strings.
  getApplicableNorms: (taskId: number): Promise<any> => {
    return makeApiCall({ url: `/machinery-norms/task/${taskId}/applicable`, method: "get" });
  },
  // Read-only machinery requirement rolled up per norm to a hierarchy node.
  getActivityMachineryNorms: (activityId: number): Promise<any> => {
    return makeApiCall({ url: `/machinery-norms/activity/${activityId}/machinery`, method: "get" });
  },
  getPhaseMachineryNorms: (phaseId: number): Promise<any> => {
    return makeApiCall({ url: `/machinery-norms/phase/${phaseId}/machinery`, method: "get" });
  },
  getProjectMachineryNorms: (projectId: number): Promise<any> => {
    return makeApiCall({ url: `/machinery-norms/project/${projectId}/machinery`, method: "get" });
  },
  // Bulk apply-machinery-to-tasks staged upload (rows: TaskID, Activity Name,
  // Norm Code, Description, OverrideQty). Activity Name is a required
  // cross-check against the task's activity; the norm resolves via the task's
  // activity and the Description column disambiguates when the same code
  // exists twice under it.
  applyMachineryExcel: (data: FormData): Promise<any> => {
    return makeApiCall({
      url: "/machinery-norms/upload/applyMachineryExcel",
      method: "post",
      data,
    });
  },
};

export default machineryApis;
